import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import QrScanner from 'qr-scanner';
import React, { useEffect, useRef, useState } from 'react';
import QuestionForm from './QuestionForm';
import { useNavigate } from 'react-router-dom';
import { Questionaire } from 'src/common/keyText';

const QRScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const [goToQuestion, setGoToQuestion] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [patientId, setPatientId] = useState<string>('');
  const touchStartX = useRef(0);

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    // Block browser back button
    const handlePopState = (e: PopStateEvent) => {
      window.history.pushState(null, '', window.location.href);
      navigate(window.location.pathname, { replace: true });
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current < 30 || touchStartX.current > window.innerWidth - 30) {
        e.preventDefault();
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [navigate]);

  const startScanning = async () => {
    setScanning(true);

    if (videoRef.current && !qrScannerRef.current) {
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          console.log(result);
          const data = result.data.split('&');
          if (data.length === 3 && data[0] === '/question-answer-qr') {
            stopScanning();
            setGoToQuestion(true);
            setPatientId(data[1]);
          }
        },
        {
          // optional options
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );
      await qrScannerRef.current?.start();
    }
  };

  const stopScanning = async () => {
    await qrScannerRef.current?.stop();
    qrScannerRef.current = null;
    const highlights = document.querySelectorAll('.scan-region-highlight');
    highlights.forEach((highlight) => highlight.remove());
    setScanning(false);
  };

  const handleSubmit = () => {
    setSubmitSuccess(true);
  };

  return (
    <Container
      maxWidth='sm'
      sx={{
        overflowX: 'hidden',
        touchAction: 'pan-y',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 'auto',
      }}
    >
      <Box
        sx={{
          my: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {goToQuestion && submitSuccess && (
          <Stack justifyContent={'center'} alignItems={'end'}>
            <Button
              variant='outlined'
              onClick={() => {
                setGoToQuestion(false);
                setSubmitSuccess(false);
              }}
              sx={{ width: '200px', mb: 2 }}
            >
              ホームページに戻る
            </Button>
          </Stack>
        )}
        <Typography
          variant='h4'
          component='h1'
          gutterBottom
          textAlign={'center'}
        >
          {Questionaire.Title}
        </Typography>
        {!goToQuestion ? (
          <>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '300px',
                display: scanning ? '' : 'none',
              }}
            />

            {!scanning ? (
              <Button
                variant='contained'
                color='primary'
                startIcon={<QrCodeScannerIcon />}
                onClick={startScanning}
                size='large'
                sx={{ mt: 2, py: 1.5, px: 3, fontSize: '1.1rem' }}
              >
                QRコードをスキャン
              </Button>
            ) : (
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 2,
                }}
              >
                <Button
                  variant='outlined'
                  onClick={stopScanning}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  キャンセル
                </Button>
              </Paper>
            )}
          </>
        ) : !submitSuccess ? (
          <QuestionForm handleSubmit={handleSubmit} patientId={patientId} />
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mt: 2,
              borderRadius: 2,
            }}
          >
            <Typography gutterBottom>
              ご協力ありがとうございました。
              <br />
              タブレットをクリニックスタッフにお返しください。
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default QRScannerPage;
